import { ApiHatasi, bicimsizYanitHatasi } from './hata';
import { erisimJetonuAl, erisimJetonuSil, erisimJetonuYaz } from './jeton-deposu';
import type { HataYanit } from './tipler';

/**
 * İSTEK KATMANI.
 *
 * Üç işi vardır ve üçü de her çağrıda elle yapılamayacak kadar hataya açıktır:
 *   1. Erişim jetonunu başlığa koymak
 *   2. Hata gövdesini `ApiHatasi`ya çevirip fırlatmak
 *   3. 401'de jetonu BİR KEZ yenileyip isteği tekrarlamak
 */

/** Tarayıcının istek attığı taban. Vite vekili yüzünden görelidir. */
const API_TABANI: string = import.meta.env.VITE_API_TABANI ?? '/api';

/** Oturum kaydına yazılır; pilot göstergelerinde panel/saha ayrımı için. */
const ISTEMCI = 'panel' as const;

/** Yenileme ucu — sürümsüzdür, çerezin `Path` niteliği buna bağlı. */
const YENILEME_YOLU = '/kimlik/yenile';

type Yontem = 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE';

export interface IstekSecenekleri {
  yontem?: Yontem;
  govde?: unknown;
  sorgu?: Record<string, string | number | boolean | null | undefined>;
  imza?: AbortSignal;
  /**
   * Yenileme denemesi yapılmaz. Kimlik uçlarında ZORUNLU: `/giris` 401
   * verdiğinde yenileme denemek anlamsızdır, `/yenile` 401 verdiğinde ise
   * sonsuz döngü olur.
   */
  yenilemeYok?: boolean;
}

/* ------------------------------------------------------------------ */
/* Oturum olayları                                                      */
/* ------------------------------------------------------------------ */

type OturumDinleyicisi = () => void;

const dusmeDinleyicileri = new Set<OturumDinleyicisi>();

/**
 * Oturum kurtarılamadığında çağrılır (yenileme de başarısız).
 *
 * İstek katmanı router'a doğrudan bağlanmaz; `useNavigate` React ağacının
 * dışından çağrılamaz. Dinleyici deseni bu katmanı React'ten bağımsız tutar.
 */
export function oturumDusunceDinle(dinleyici: OturumDinleyicisi): () => void {
  dusmeDinleyicileri.add(dinleyici);
  return () => dusmeDinleyicileri.delete(dinleyici);
}

function oturumDustuguDuyur(): void {
  erisimJetonuSil();
  for (const dinleyici of dusmeDinleyicileri) dinleyici();
}

/* ------------------------------------------------------------------ */
/* Yenileme — TEKİLLEŞTİRİLMİŞ                                          */
/* ------------------------------------------------------------------ */

/**
 * SUNUCU YENİLEMEYİ DÖNDÜRMELİ YAPAR.
 *
 * Her `/yenile` çağrısı eski jetonu iptal eder. Aynı anda iki çağrı giderse
 * ikincisi "kullanılmış jeton yeniden gönderildi" sayılır ve sunucu TÜM
 * oturum ailesini düşürür — kullanıcı sebepsizce çıkışa atılır.
 *
 * Sayfa açılışında birden çok sorgu aynı anda 401 alır; bu tam olarak o
 * senaryodur. Bu yüzden aynı anda EN FAZLA BİR yenileme sözü bulunur,
 * bekleyen herkes aynı sözü bekler.
 */
export interface YenilemeSonucu<T = unknown> {
  erisimJetonu: string;
  /**
   * Sunucunun döndürdüğü kullanıcı nesnesi. İstek katmanı bu nesnenin
   * şeklini BİLMEZ — kimlik sözleşmesi `api/kimlik.ts`te yaşar. Burada
   * tiplenseydi taban katman kimlik modülüne bağlanır ve döngüsel bağımlılık
   * oluşurdu.
   */
  kullanici: T;
}

let yenilemeSozu: Promise<YenilemeSonucu> | null = null;

async function yenilemeyiCalistir(): Promise<YenilemeSonucu> {
  let yanit: Response;

  try {
    yanit = await fetch(`${API_TABANI}${YENILEME_YOLU}`, {
      method: 'POST',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ istemci: ISTEMCI }),
    });
  } catch {
    throw ApiHatasi.agKopmasi(YENILEME_YOLU);
  }

  if (!yanit.ok) {
    throw await hataUret(yanit, YENILEME_YOLU);
  }

  const govde = (await yanit.json()) as { erisimJetonu?: unknown; kullanici?: unknown };

  if (typeof govde.erisimJetonu !== 'string') {
    throw bicimsizYanitHatasi(yanit.status, YENILEME_YOLU);
  }

  erisimJetonuYaz(govde.erisimJetonu);
  return { erisimJetonu: govde.erisimJetonu, kullanici: govde.kullanici };
}

/**
 * Jetonu yeniler. Eşzamanlı çağrılar tek bir isteği paylaşır.
 *
 * Uygulama açılışında oturumu geri kurmak için de kullanılır: erişim jetonu
 * bellekte olduğu için sayfa yenilendiğinde kaybolur, çerez ise durur.
 *
 * KULLANICI NESNESİ DE DÖNER. Yalnız jeton döndürseydi, açılışta kullanıcı
 * bilgisini almak için AYRI bir `/yenile` çağrısı gerekirdi — bu İKİNCİ bir
 * rotasyon demek ve sunucu her rotasyonda bir öncekini iptal ediyor.
 */
export function jetonYenile<T = unknown>(): Promise<YenilemeSonucu<T>> {
  yenilemeSozu ??= yenilemeyiCalistir().finally(() => {
    // Söz çözülür çözülmez temizlenir; aksi halde ilk yenilemeden sonraki
    // tüm 401'ler aynı ESKİ sözü bekler ve hiç yenilenmez.
    yenilemeSozu = null;
  });

  return yenilemeSozu as Promise<YenilemeSonucu<T>>;
}

/* ------------------------------------------------------------------ */
/* İstek                                                               */
/* ------------------------------------------------------------------ */

function sorguEkle(yol: string, sorgu: IstekSecenekleri['sorgu']): string {
  if (!sorgu) return yol;

  const parametreler = new URLSearchParams();
  for (const [anahtar, deger] of Object.entries(sorgu)) {
    if (deger === undefined || deger === null || deger === '') continue;
    parametreler.set(anahtar, String(deger));
  }

  const metin = parametreler.toString();
  return metin ? `${yol}?${metin}` : yol;
}

async function hataUret(yanit: Response, yol: string): Promise<ApiHatasi> {
  let govde: unknown;

  try {
    govde = await yanit.json();
  } catch {
    return bicimsizYanitHatasi(yanit.status, yol);
  }

  // Sunucu sözleşmeye uymayan bir gövde döndürdüyse (vekil hata sayfası,
  // yük dengeleyici) alanları uydurmak yerine bicimsiz hata verilir.
  if (typeof govde !== 'object' || govde === null || !('mesaj' in govde)) {
    return bicimsizYanitHatasi(yanit.status, yol);
  }

  const hata = govde as Partial<HataYanit>;

  return new ApiHatasi({
    baslik: hata.baslik ?? 'Hata',
    mesaj: hata.mesaj ?? 'İşlem tamamlanamadı.',
    kod: hata.kod,
    durum: hata.durum ?? yanit.status,
    yol: hata.yol ?? yol,
    zaman: hata.zaman ?? new Date().toISOString(),
    alanlar: hata.alanlar,
  });
}

interface CiplakSonuc {
  yanit: Response;
  /** İstek gönderilirken kullanılan jeton — kuşak denetimi için. */
  kullanilanJeton: string | null;
}

async function ciplakIstek(yol: string, secenekler: IstekSecenekleri): Promise<CiplakSonuc> {
  const jeton = erisimJetonuAl();
  const basliklar: Record<string, string> = {};

  if (jeton) basliklar.Authorization = `Bearer ${jeton}`;
  if (secenekler.govde !== undefined) basliklar['Content-Type'] = 'application/json';

  const yanit = await fetch(`${API_TABANI}${sorguEkle(yol, secenekler.sorgu)}`, {
    method: secenekler.yontem ?? 'GET',
    // Yenileme çerezinin gidebilmesi için ZORUNLU.
    credentials: 'include',
    headers: basliklar,
    body: secenekler.govde === undefined ? undefined : JSON.stringify(secenekler.govde),
    signal: secenekler.imza,
  });

  return { yanit, kullanilanJeton: jeton };
}

/**
 * Tipli API isteği.
 *
 * @param yol `/api` SONRASI yol — `/v1/personel` veya `/kimlik/ben`.
 */
export async function istek<T>(yol: string, secenekler: IstekSecenekleri = {}): Promise<T> {
  let sonuc: CiplakSonuc;

  try {
    sonuc = await ciplakIstek(yol, secenekler);
  } catch (hata) {
    // İptal edilen istek hata değildir; TanStack Query bunu kendi ele alır.
    if (hata instanceof DOMException && hata.name === 'AbortError') throw hata;
    throw ApiHatasi.agKopmasi(yol);
  }

  let yanit = sonuc.yanit;

  if (yanit.status === 401 && !secenekler.yenilemeYok) {
    /*
     * JETON KUŞAĞI DENETİMİ.
     *
     * Bu istek yola çıktıktan sonra başka bir istek jetonu çoktan
     * yenilemiş olabilir. O durumda burada tekrar yenilemek İKİNCİ bir
     * rotasyon başlatır: sunucu her yenilemede eski jetonu iptal ettiği
     * için gereksiz iptal zinciri oluşur ve yük altında büyür.
     *
     * Depodaki jeton, isteğin kullandığından farklıysa yenileme atlanır
     * ve istek doğrudan yeni jetonla tekrarlanır.
     */
    const jetonDegisti = erisimJetonuAl() !== sonuc.kullanilanJeton;

    if (!jetonDegisti) {
      try {
        await jetonYenile();
      } catch {
        // Yenileme de başarısız — oturum gerçekten bitti.
        oturumDustuguDuyur();
        throw await hataUret(yanit, yol);
      }
    }

    try {
      sonuc = await ciplakIstek(yol, secenekler);
    } catch (hata) {
      if (hata instanceof DOMException && hata.name === 'AbortError') throw hata;
      throw ApiHatasi.agKopmasi(yol);
    }

    yanit = sonuc.yanit;

    // Yenilenmiş jetonla da 401 alınıyorsa tekrar denemenin anlamı yok.
    if (yanit.status === 401) {
      oturumDustuguDuyur();
      throw await hataUret(yanit, yol);
    }
  }

  if (!yanit.ok) throw await hataUret(yanit, yol);

  // 204 ve gövdesiz 200'ler. `json()` çağrılırsa ayrıştırma hatası verir.
  if (yanit.status === 204 || yanit.headers.get('Content-Length') === '0') {
    return undefined as T;
  }

  try {
    return (await yanit.json()) as T;
  } catch {
    return undefined as T;
  }
}

/** Yalnız kimlik uçları için: yenileme denemesi yapılmadan istek. */
export function kimlikIstegi<T>(
  yol: string,
  secenekler: Omit<IstekSecenekleri, 'yenilemeYok'> = {},
): Promise<T> {
  return istek<T>(yol, { ...secenekler, yenilemeYok: true });
}

export { API_TABANI, ISTEMCI };
